import { Link } from "components/commons/Link";
import { useTranslations } from "next-intl";
import dynamic from "next/dynamic";
import { useRouter } from "next/router";
import { ReactNode } from "react";
import { twMerge } from "tailwind-merge";
import { localizePath } from "src/utils/localizedRoutes";
import DocsSearch from "./DocsSearch";
const SideMenu = dynamic(() => import("./SideMenu"), {
  ssr: false,
});

type FeaturesLayoutProps = {
  children: ReactNode;
};

type NavLinkProps = {
  href: string;
  name: string;
};

const FeaturesLayout = ({ children }: FeaturesLayoutProps) => {
  const t = useTranslations("layout/features");

  const router = useRouter();

  const links = [
    {
      id: 0,
      group: {
        to: "/software/novidades",
        name: t("link-title-novidades"),
      },
      items: [],
    },
    {
      id: 1,
      group: {
        to: "/software/orcamentacao",
        name: t("link-title-orcamentacao"),
      },
      items: [
        {
          to: "/software/orcamentacao/criar-orcamento",
          name: t("link-title-orcamentacao-1"),
          id: 1,
        },
        {
          to: "/software/orcamentacao/efetuar-ou-verificar-as-medicoes",
          name: t("link-title-orcamentacao-2"),
          id: 2,
        },
        {
          to: "/software/orcamentacao/importar-medicoes-de-modelos-bim",
          name: t("link-title-orcamentacao-3"),
          id: 3,
        },
        {
          to: "/software/orcamentacao/compor-os-precos-do-orcamento",
          name: t("link-title-orcamentacao-4"),
          id: 4,
        },
        {
          to: "/software/orcamentacao/introduzir-os-precos-diretos",
          name: t("link-title-orcamentacao-5"),
          id: 5,
        },
        {
          to: "/software/orcamentacao/elaborar-mapas-comparativos",
          name: t("link-title-orcamentacao-6"),
          id: 6,
        },
        {
          to: "/software/orcamentacao/introduzir-margens-no-orcamento",
          name: t("link-title-orcamentacao-7"),
          id: 7,
        },
        {
          to: "/software/orcamentacao/analisar-e-otimizar-o-orcamento",
          name: t("link-title-orcamentacao-8"),
          id: 8,
        },
        {
          to: "/software/orcamentacao/propriedades-e-impressao",
          name: t("link-title-orcamentacao-9"),
          id: 9,
        },
      ],
    },
    {
      id: 2,
      group: {
        to: "/software/gestao-de-obra",
        name: t("link-title-gestao-de-obra"),
      },
      items: [
        {
          to: "/software/gestao-de-obra/elaborar-os-autos-de-medicao",
          name: t("link-title-gestao-de-obra-1"),
          id: 1,
        },
        {
          to: "/software/gestao-de-obra/controlar-as-compras",
          name: t("link-title-gestao-de-obra-2"),
          id: 2,
        },
        {
          to: "/software/gestao-de-obra/elaborar-e-gerir-os-trabalhos-a-mais",
          name: t("link-title-gestao-de-obra-3"),
          id: 3,
        },
        {
          to: "/software/gestao-de-obra/controlar-os-custos-da-obra",
          name: t("link-title-gestao-de-obra-4"),
          id: 4,
        },
        {
          to: "/software/gestao-de-obra/elaborar-o-planeamento",
          name: t("link-title-gestao-de-obra-5"),
          id: 5,
        },
        {
          to: "/software/gestao-de-obra/reorcamentacao",
          name: t("link-title-gestao-de-obra-6"),
          id: 6,
        },
        {
          to: "/software/gestao-de-obra/elaborar-o-bloco-de-notas",
          name: t("link-title-gestao-de-obra-7"),
          id: 7,
        },
        {
          to: "/software/gestao-de-obra/gerir-a-lista-de-fornecedores",
          name: t("link-title-gestao-de-obra-8"),
          id: 8,
        },
        {
          to: "/software/gestao-de-obra/revisao-de-precos",
          name: t("link-title-gestao-de-obra-9"),
          id: 9,
        },
      ],
    },
    {
      id: 3,
      group: { to: "/software/planeamento", name: t("link-title-planeamento") },
      items: [
        {
          to: "/software/planeamento/elaborar-o-plano-de-trabalhos",
          name: t("link-title-planeamento-1"),
          id: 1,
        },
        {
          to: "/software/planeamento/plano-de-mao-de-obra-e-equipamentos",
          name: t("link-title-planeamento-2"),
          id: 2,
        },
        {
          to: "/software/planeamento/obter-o-plano-de-pagamentos",
          name: t("link-title-planeamento-3"),
          id: 3,
        },
        {
          to: "/software/planeamento/criar-a-memoria-descritiva",
          name: t("link-title-planeamento-4"),
          id: 4,
        },
        {
          to: "/software/planeamento/criar-o-plano-de-compras",
          name: t("link-title-planeamento-5"),
          id: 5,
        },
        {
          to: "/software/planeamento/planeamento-externo",
          name: t("link-title-planeamento-6"),
          id: 6,
        },
      ],
    },
  ];

  const Group = ({ id, group, items }: any) => {
    return (
      <li>
        {/* <label className='font-extrabold text-black text-md'>{name}</label> */}
        <Link
          href={localizePath(group.to, router.locale)}
          text={group.name}
          className={twMerge(
            "font-extrabold text-black text-md",
            router.pathname === group.to && "text-blue hover:text-blue",
          )}
          prefetch={false}
        />
        <ul key={id} className="space-y-1 border-l pl-4 m-1 border-gray-800">
          {items.map(({ id, to, name }: any) => (
            <li key={id}>
              <NavLink href={to} name={name} />
            </li>
          ))}
        </ul>
      </li>
    );
  };

  const NavLink = ({ href, name }: NavLinkProps) => {
    return (
      <div className="relative">
        <div
          className={twMerge(
            "absolute top-2.5 -left-5 rounded-full h-2 w-2 ",
            router.pathname === href && "bg-blue",
          )}
        />
        <Link
          href={localizePath(href, router.locale)}
          text={name}
          className={twMerge(
            "text-sm text-gray-900 font-semibold hover:text-black",
            router.pathname === href && "text-blue hover:text-blue",
          )}
          prefetch={false}
        />
      </div>
    );
  };

  return (
    <div className="relative bg-features">
      <div className="relative flex justify-center max-w-9xl mx-auto min-h-100vh">
        <div className="relative hidden lg:block">
          <div className="sticky top-0 h-[calc(100vh)] overflow-y-auto overflow-x-hidden pt-32 pb-12 px-4 ">
            <DocsSearch className="mb-4" />
            <ul className="space-y-4">
              {links.map(({ id, group, items }: any) => (
                <Group key={id} group={group} items={items} />
              ))}
            </ul>
          </div>
        </div>
        <div className="max-w-6xl flex-auto bg-white">{children}</div>
      </div>
      <div className="fixed bottom-3 right-3">
        <SideMenu links={links}></SideMenu>
      </div>
    </div>
  );
};

export default FeaturesLayout;
